import { ComponentType } from "react";

import { getCurrentBrand } from "src/brand/currentBrand";
import {
  ClientCreationModalEC,
  ClientCreationModalProps,
} from "src/brand/EC/components/create-client/CreateClientModalEC";
import { DocumentsUploadFieldsetEC } from "src/brand/EC/components/folder/DocumentsUploadFieldset";
import { FolderContentEC } from "src/brand/EC/components/folder/FolderContent";
import { FolderCardRiskEC } from "src/brand/EC/components/folder/version/FolderCardRisk";
import type { BrandId } from "src/core/constants/brand";
import { FolderFile } from "src/core/gql/graphql";

export type OverrideTokens = {
  "components.folder.FolderContent": ComponentType;
  "components.folder.ClientCreationModal": ComponentType<ClientCreationModalProps>;
  "components.folder.version.FolderCardRisk": ComponentType<{ folderFile: FolderFile }>;
  "components.folder.DocumentsUploadFieldset": ComponentType<{
    requiredDocuments: { code: string; label: string }[];
    disabled?: boolean;
    incompleteFields?: Set<string>;
    className?: string;
    handlePreview?: (document: { id: string; name: string }) => void;
  }>;
};

// Props “brutes” extraites du ComponentType
type RawTokenProps<T extends keyof OverrideTokens> = OverrideTokens[T] extends ComponentType<
  infer P
>
  ? P
  : never;

// Props finales gérées par React (key/ref/defaultProps/propTypes)
export type TokenProps<T extends keyof OverrideTokens> = JSX.LibraryManagedAttributes<
  OverrideTokens[T],
  Extract<RawTokenProps<T>, object>
>;

type BrandOverrides = Partial<OverrideTokens>;

const createBrandOverrides = <T extends BrandOverrides>(o: T) => o;

const EC_OVERRIDES = createBrandOverrides({
  "components.folder.FolderContent": FolderContentEC,
  "components.folder.ClientCreationModal": ClientCreationModalEC,
  "components.folder.version.FolderCardRisk": FolderCardRiskEC,
  "components.folder.DocumentsUploadFieldset": DocumentsUploadFieldsetEC,
} satisfies BrandOverrides);

const REGISTRY: Record<BrandId, BrandOverrides> = {
  EC: EC_OVERRIDES,
};

export const getOverride = <T extends keyof OverrideTokens>(
  token: T
): OverrideTokens[T] | undefined => {
  const brand = getCurrentBrand();
  if (!brand) return undefined;
  return REGISTRY[brand]?.[token];
};

export const getOverrideFrom = <T extends keyof OverrideTokens>(
  brand: BrandId | null | undefined,
  token: T
): OverrideTokens[T] | undefined => {
  if (!brand) {
    return undefined;
  }

  return REGISTRY[brand][token];
};
